import { ChangeEvent, ReactNode } from "react";
import { ViewItem } from "../../../Components/Views/ViewItem";
import { controllers } from "../../../Controllers/Controllers";
import { WorkerActivityStatsType } from "../../../DataTypes/activity";
import './activity-dashboard-styles.css'

const daysAgoISO = (n: number) => {
    const d = new Date()
    d.setDate(d.getDate() - n)
    return d.toISOString().slice(0, 10)
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const formatHoursMinutes = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.round((seconds % 3600) / 60)
    return `${h}h ${m}m`
}

const formatDateShort = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })

const formatDateWeekday = (iso: string) =>
    new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })

const formatHourLabel = (hour: number) => {
    if (hour === 0) return '12am'
    if (hour === 12) return '12pm'
    return hour < 12 ? `${hour}am` : `${hour - 12}pm`
}

const timeAgo = (isoString: string) => {
    const diffMs = Date.now() - new Date(isoString).getTime()
    const mins = Math.floor(diffMs / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    return new Date(isoString).toLocaleDateString()
}

const formatExactTime = (isoString: string) =>
    new Date(isoString).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

const formatExactDateTime = (isoString: string) =>
    new Date(isoString).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
    })

const HOUR_LABEL_STEPS = [0, 3, 6, 9, 12, 15, 18, 21]

const heatLevel = (count: number, maxValue: number) => {
    if (count === 0) return 0
    const ratio = count / maxValue
    if (ratio > 0.66) return 3
    if (ratio > 0.33) return 2
    return 1
}

const CHART_W = 600
const CHART_H = 90
const CHART_PAD_X = 2
const CHART_PAD_Y = 6

const computePoints = (values: number[], maxValue: number) => {
    const n = values.length
    if (n === 0) return []
    const usableW = CHART_W - CHART_PAD_X * 2
    const usableH = CHART_H - CHART_PAD_Y * 2
    const stepX = n > 1 ? usableW / (n - 1) : 0
    return values.map((v, i) => ({
        x: CHART_PAD_X + stepX * i,
        y: CHART_PAD_Y + usableH - (maxValue > 0 ? (v / maxValue) * usableH : 0),
    }))
}

const pointsToPath = (points: { x: number; y: number }[]) =>
    points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')

type HoveredCell = { workerId: number; date: string; hour: number; count: number; passiveCount: number } | null
type HoveredTrendPoint = { workerId: number; index: number } | null

type ActivityDashboardState = {
    workers: WorkerActivityStatsType[];
    startDate: string;
    endDate: string;
    loading: boolean;
    error: string | null;
    needsAuth: boolean;
    notAuthorized: boolean;
    hoveredCell: HoveredCell;
    hoveredTrendPoint: HoveredTrendPoint;
}

class ActivityDashboardPage extends ViewItem {
    state: ActivityDashboardState;

    constructor(props: any) {
        super(props)
        this.state = {
            workers: [],
            startDate: daysAgoISO(6),
            endDate: todayISO(),
            loading: true,
            error: null,
            needsAuth: false,
            notAuthorized: false,
            hoveredCell: null,
            hoveredTrendPoint: null,
        }
    }

    componentDidMount(): void {
        if (!controllers().auth.isAdmin) {
            this.setState({ loading: false, notAuthorized: true })
            return
        }
        this.loadDashboard()
    }

    loadDashboard = async () => {
        const { startDate, endDate } = this.state
        this.setState({ loading: true, error: null, needsAuth: false })
        try {
            if (!controllers().auth.userToken) {
                this.setState({ loading: false, needsAuth: true })
                return
            }
            const response = await controllers().outreach.getActivityDashboard({
                start_date: startDate,
                end_date: endDate,
            })
            if (!response || response.statusCode !== 200) {
                this.setState({ loading: false, error: response?.error || 'Failed to load activity data.' })
                return
            }
            this.setState({ workers: response.data?.workers || [], loading: false })
        } catch (e) {
            this.setState({ loading: false, error: 'Failed to load activity data.' })
        }
    }

    handleStartDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ startDate: e.target.value }, this.loadDashboard)
    }

    handleEndDateChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ endDate: e.target.value }, this.loadDashboard)
    }

    handleCellHover = (workerId: number, date: string, hour: number, count: number, passiveCount: number) => {
        this.setState({ hoveredCell: { workerId, date, hour, count, passiveCount } })
    }

    handleCellLeave = () => {
        this.setState({ hoveredCell: null })
    }

    handleTrendPointerMove = (workerId: number, clientX: number, containerRect: DOMRect, dayCount: number) => {
        if (dayCount === 0) return
        const relativeX = (clientX - containerRect.left) / containerRect.width
        const index = Math.round(relativeX * (dayCount - 1))
        const clamped = Math.max(0, Math.min(dayCount - 1, index))
        this.setState({ hoveredTrendPoint: { workerId, index: clamped } })
    }

    handleTrendPointerLeave = () => {
        this.setState({ hoveredTrendPoint: null })
    }

    renderHeatmap = (worker: WorkerActivityStatsType) => {
        const { hoveredCell } = this.state
        const maxCellValue = Math.max(1, ...worker.daily_activity.flatMap(d => d.hourly_counts || []))
        const isHoveredHere = hoveredCell?.workerId === worker.user.id

        return (
            <div className="activity-heatmap-block">
                <div className="activity-heatmap-readout">
                    {isHoveredHere && hoveredCell ? (
                        <>
                            <strong>{formatDateWeekday(hoveredCell.date)}</strong>
                            <span>{formatHourLabel(hoveredCell.hour)}–{formatHourLabel((hoveredCell.hour + 1) % 24)}</span>
                            <span>{hoveredCell.count} action{hoveredCell.count === 1 ? '' : 's'}</span>
                            {hoveredCell.passiveCount > 0 && (
                                <span className="activity-heatmap-readout-passive">
                                    {hoveredCell.count === 0
                                        ? 'Online, no changes made'
                                        : `+${hoveredCell.passiveCount} online ping${hoveredCell.passiveCount === 1 ? '' : 's'}`}
                                </span>
                            )}
                        </>
                    ) : (
                        <span className="activity-heatmap-readout-hint">Hover a cell to see the exact hour and count</span>
                    )}
                </div>

                <div className="activity-heatmap-legend">
                    <span className="activity-heatmap-legend-item">
                        <span className="activity-heatmap-legend-swatch activity-heatmap-legend-swatch-action" />
                        Action taken
                    </span>
                    <span className="activity-heatmap-legend-item">
                        <span className="activity-heatmap-legend-swatch activity-heatmap-legend-swatch-passive" />
                        Online, no changes
                    </span>
                </div>

                <div className="activity-heatmap-scroll">
                    <div className="activity-heatmap-hour-axis">
                        <span className="activity-heatmap-corner" />
                        {Array.from({ length: 24 }).map((_, hour) => (
                            <span key={hour} className="activity-heatmap-hour-label">
                                {HOUR_LABEL_STEPS.includes(hour) ? formatHourLabel(hour) : ''}
                            </span>
                        ))}
                    </div>
                    {worker.daily_activity.map(day => {
                        const hourlyCounts = day.hourly_counts || Array(24).fill(0)
                        const hourlyPassiveCounts = day.hourly_passive_counts || Array(24).fill(0)
                        return (
                            <div key={day.date} className="activity-heatmap-row">
                                <span className="activity-heatmap-date-label">{formatDateShort(day.date)}</span>
                                {hourlyCounts.map((count, hour) => {
                                    const passiveCount = hourlyPassiveCounts[hour] || 0
                                    const isPassiveOnly = count === 0 && passiveCount > 0
                                    const level = heatLevel(count, maxCellValue)
                                    const cellHovered = isHoveredHere && hoveredCell!.date === day.date && hoveredCell!.hour === hour
                                    return (
                                        <span
                                            key={hour}
                                            className={`activity-heatmap-cell ${isPassiveOnly ? 'activity-heatmap-cell-passive' : `activity-heatmap-cell-level-${level}`} ${cellHovered ? 'activity-heatmap-cell-hovered' : ''}`}
                                            onMouseEnter={() => this.handleCellHover(worker.user.id, day.date, hour, count, passiveCount)}
                                            onMouseLeave={this.handleCellLeave}
                                        />
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    renderTrendLine = (worker: WorkerActivityStatsType) => {
        const { hoveredTrendPoint } = this.state
        const daily = worker.daily_activity
        const eventValues = daily.map(d => d.event_count)
        const activeMinuteValues = daily.map(d => Math.round(d.active_seconds / 60))
        const maxValue = Math.max(1, ...eventValues, ...activeMinuteValues)

        const eventPoints = computePoints(eventValues, maxValue)
        const activePoints = computePoints(activeMinuteValues, maxValue)
        const eventLine = pointsToPath(eventPoints)
        const activeLine = pointsToPath(activePoints)

        const labelIndices = daily.length > 2 ? [0, Math.floor((daily.length - 1) / 2), daily.length - 1] : daily.map((_, i) => i)

        const isHoveredHere = hoveredTrendPoint?.workerId === worker.user.id
        const hoveredIndex = isHoveredHere ? hoveredTrendPoint!.index : null
        const hoveredDay = hoveredIndex !== null ? daily[hoveredIndex] : null
        const hoveredEventPoint = hoveredIndex !== null ? eventPoints[hoveredIndex] : null
        const hoveredActivePoint = hoveredIndex !== null ? activePoints[hoveredIndex] : null
        const tooltipLeftPct = hoveredEventPoint ? (hoveredEventPoint.x / CHART_W) * 100 : 0
        const tooltipEdgeClass = tooltipLeftPct < 12 ? 'activity-trend-tooltip-left' : tooltipLeftPct > 88 ? 'activity-trend-tooltip-right' : ''

        return (
            <div className="activity-trend-block">
                <div className="activity-trend-legend">
                    <span className="activity-legend-item"><span className="activity-legend-dot activity-legend-events" />Actions logged</span>
                    <span className="activity-legend-item"><span className="activity-legend-dot activity-legend-minutes" />Active minutes (actions only, est.)</span>
                </div>
                <div
                    className="activity-trend-hover-area"
                    onMouseMove={(e) => this.handleTrendPointerMove(worker.user.id, e.clientX, e.currentTarget.getBoundingClientRect(), daily.length)}
                    onMouseLeave={this.handleTrendPointerLeave}
                >
                    <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" className="activity-trend-svg">
                        <path d={activeLine} className="activity-trend-line-minutes" fill="none" />
                        <path d={eventLine} className="activity-trend-line-events" fill="none" />
                        {hoveredEventPoint && (
                            <>
                                <line x1={hoveredEventPoint.x} y1={CHART_PAD_Y} x2={hoveredEventPoint.x} y2={CHART_H - CHART_PAD_Y} className="activity-trend-crosshair" />
                                <circle cx={hoveredEventPoint.x} cy={hoveredEventPoint.y} r="3.5" className="activity-trend-dot activity-trend-dot-events" />
                                {hoveredActivePoint && (
                                    <circle cx={hoveredActivePoint.x} cy={hoveredActivePoint.y} r="3.5" className="activity-trend-dot activity-trend-dot-minutes" />
                                )}
                            </>
                        )}
                    </svg>
                    {hoveredDay && (
                        <div className={`activity-trend-tooltip ${tooltipEdgeClass}`} style={{ left: `${tooltipLeftPct}%` }}>
                            <div className="activity-trend-tooltip-date">{formatDateWeekday(hoveredDay.date)}</div>
                            <div className="activity-trend-tooltip-row">
                                <span className="activity-legend-dot activity-legend-events" />Actions <strong>{hoveredDay.event_count}</strong>
                            </div>
                            <div className="activity-trend-tooltip-row">
                                <span className="activity-legend-dot activity-legend-minutes" />Active <strong>{formatHoursMinutes(hoveredDay.active_seconds)}</strong>
                            </div>
                            <div className="activity-trend-tooltip-row">
                                Active incl. online <strong>{formatHoursMinutes(hoveredDay.active_seconds_with_passive)}</strong>
                            </div>
                            {hoveredDay.first_action_at && (
                                <div className="activity-trend-tooltip-window">{hoveredDay.first_action_at} – {hoveredDay.last_action_at}</div>
                            )}
                        </div>
                    )}
                </div>
                <div className="activity-trend-axis">
                    {labelIndices.map(i => (
                        <span key={i} className="activity-trend-axis-label">{formatDateShort(daily[i].date)}</span>
                    ))}
                </div>
            </div>
        )
    }

    renderActionBreakdown = (worker: WorkerActivityStatsType) => {
        const maxCount = Math.max(1, ...worker.action_breakdown.map(a => a.count))
        return (
            <div className="activity-breakdown-block">
                <h4 className="activity-subhead">What was done</h4>
                {worker.action_breakdown.length === 0 ? (
                    <div className="activity-empty">No actions logged in this period.</div>
                ) : (
                    <div className="activity-breakdown-list">
                        {worker.action_breakdown.map(item => (
                            <div key={item.action} className="activity-breakdown-row">
                                <span className="activity-breakdown-label">{item.label}</span>
                                <div className="activity-breakdown-bar-track">
                                    <div className="activity-breakdown-bar" style={{ width: `${(item.count / maxCount) * 100}%` }} />
                                </div>
                                <span className="activity-breakdown-count">{item.count}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    renderLogFeed = (worker: WorkerActivityStatsType) => {
        // console.log("Worker", worker.user, JSON.stringify(worker.recent_log_entries))
        return (
            <div className="activity-log-block">
                <h4 className="activity-subhead">Recent actions</h4>
                {worker.recent_log_entries.length === 0 ? (
                    <div className="activity-empty">Nothing logged in this period.</div>
                ) : (
                    <div className="activity-log-list">
                        {worker.recent_log_entries.map((entry, i) => (
                            <div key={i} className="activity-log-row">
                                <span className="activity-log-action">{entry.label}</span>
                                <span className="activity-log-target">{entry.target_repr || '—'}</span>
                                <span className="activity-log-time" title={formatExactDateTime(entry.created_at)}>
                                    {timeAgo(entry.created_at)}
                                    <span className="activity-log-time-exact">{formatExactTime(entry.created_at)}</span>
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )
    }

    renderWorkerPanel = (worker: WorkerActivityStatsType) => {
        return (
            <div key={worker.user.id} className="activity-worker-panel">
                <div className="activity-worker-header">
                    <div>
                        <span className="activity-worker-name">{worker.user.username || worker.user.email}</span>
                        <div className="activity-worker-email">{worker.user.email}</div>
                    </div>
                    <div className="activity-worker-stats">
                        <div className="activity-stat">
                            <span className="activity-stat-value">{worker.contacted_count}</span>
                            <span className="activity-stat-label">contacted</span>
                        </div>
                        <span className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">{worker.total_events}</span>
                            <span className="activity-stat-label">actions logged</span>
                        </div>
                        <span className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">{formatHoursMinutes(worker.total_active_seconds)}</span>
                            <span className="activity-stat-label">active (actions)</span>
                        </div>
                        <span className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">{formatHoursMinutes(worker.total_active_seconds_with_passive)}</span>
                            <span className="activity-stat-label">active (incl. online)</span>
                        </div>
                        <span className="activity-stat-divider" />
                        <div className="activity-stat">
                            <span className="activity-stat-value">{worker.total_passive_pings}</span>
                            <span className="activity-stat-label">online pings</span>
                        </div>
                    </div>
                </div>

                {this.renderHeatmap(worker)}
                {this.renderTrendLine(worker)}

                <div className="activity-detail-row">
                    {this.renderActionBreakdown(worker)}
                    {this.renderLogFeed(worker)}
                </div>
            </div>
        )
    }

    render(): ReactNode {
        const { workers, startDate, endDate, loading, error, needsAuth, notAuthorized } = this.state

        return (
            <div className="activity-dashboard-page">
                <div className="activity-dashboard-header">
                    <div>
                        <h1 className="activity-dashboard-title">Worker activity</h1>
                        <span className="activity-dashboard-subtitle">
                            Raw activity logged in the CRM — a starting point for a conversation, not a verdict.
                        </span>
                    </div>
                    <div className="activity-dashboard-toolbar">
                        <label className="activity-date-field">
                            From
                            <input type="date" value={startDate} max={endDate} onChange={this.handleStartDateChange} />
                        </label>
                        <label className="activity-date-field">
                            To
                            <input type="date" value={endDate} min={startDate} max={todayISO()} onChange={this.handleEndDateChange} />
                        </label>
                    </div>
                </div>

                {notAuthorized && <div className="activity-state">Admin access required.</div>}

                {!notAuthorized && !loading && needsAuth && (
                    <div className="activity-banner activity-banner-info">
                        <span>Sign in to view activity.</span>
                        <button className="activity-banner-action" onClick={() => controllers().modals.authModal.show()}>Sign In</button>
                    </div>
                )}

                {!notAuthorized && !loading && error && (
                    <div className="activity-banner activity-banner-error">
                        <span>{error}</span>
                        <button className="activity-banner-action" onClick={this.loadDashboard}>Retry</button>
                    </div>
                )}

                {!notAuthorized && loading && <div className="activity-state"><div className="activity-spinner" /></div>}

                {!notAuthorized && !loading && !error && !needsAuth && workers.length === 0 && (
                    <div className="activity-state">No non-admin users found.</div>
                )}

                {!notAuthorized && !loading && !error && !needsAuth && workers.length > 0 && (
                    <div className="activity-worker-list">
                        {workers.map(w => this.renderWorkerPanel(w))}
                    </div>
                )}
            </div>
        )
    }
}

export { ActivityDashboardPage }